<template>
    <div class="tyn-root ia-root">
        <Header />

        <div class="ia-app">
            <!-- BARRA DE IDENTIFICAÇÃO DO DOCUMENTO -->
            <header class="ia-topbar">
                <button class="tbtn icon" title="Voltar ao processo" @click="voltar">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                </button>

                <div class="doc-id">
                    <h1>{{ interacao?.nmModeloArtefato || 'Edição assistida do documento' }}</h1>
                    <div class="meta">
                        <span class="dot" :class="statusDotClass"></span>
                        {{ statusTexto }}
                        <span class="sep">·</span>
                        {{ interacao?.dsNomeProcesso || 'Processo' }}
                    </div>
                </div>

                <div class="spacer"></div>

                <button class="tbtn" @click="setChat(!chatAberto)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M15 4v16" /></svg>
                    Assistente
                </button>

                <button class="tbtn"
                        :class="{ 'has-pending': alteracoesPendentes }"
                        :disabled="!interacao || processando || salvandoEdicao || !alteracoesPendentes"
                        :title="alteracoesPendentes ? 'Salvar suas alterações nesta interação' : 'Nenhuma alteração manual pendente'"
                        @click="salvarEdicoesManuais()">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><path d="M17 21v-8H7v8M7 3v5h8" /></svg>
                    {{ salvandoEdicao ? 'Salvando…' : 'Salvar alterações' }}
                </button>

                <button class="btn-primary" :disabled="!interacao || processando || salvandoEdicao" @click="finalizar">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                    Salvar no artefato
                </button>
            </header>

            <!-- ERRO GERAL -->
            <div v-if="errorMsg" class="ia-blocker">
                <div class="blocker-card">
                    <div class="blocker-icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
                    </div>
                    <h3>Não foi possível abrir a interação</h3>
                    <p class="motivo">{{ errorMsg }}</p>
                    <div class="blocker-actions">
                        <button class="btn-primary" @click="iniciar">Tentar novamente</button>
                        <button class="mini" @click="voltar">Voltar ao processo</button>
                    </div>
                </div>
            </div>

            <div v-else class="ia-body">
                <!-- DOCUMENTO -->
                <main class="editor-pane">
                    <div v-if="docErrorMsg" class="doc-error">
                        <span class="doc-error-text">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
                            {{ docErrorMsg }}
                        </span>
                        <button class="mini" @click="abrirDocumento(resolverUrlDocumento())">Recarregar</button>
                    </div>

                    <div class="editor-host" :class="{ 'is-loading': editorBusy }">
                        <div v-if="editorBusy" class="editor-overlay">
                            <div class="overlay-content">
                                <div class="spinner"></div>
                                <p>Carregando documento…</p>
                            </div>
                        </div>
                        <ejs-documenteditorcontainer ref="docx"
                                                     :serviceUrl="`${urlApi}/DOCXEditor/`"
                                                     :enableToolbar="true"
                                                     :toolbarItems="itensBarraFerramentas"
                                                     height="100%"
                                                     width="100%"
                                                     locale="pt-BR"
                                                     @created="onEditorCreated"
                                                     @beforeXmlHttpRequestSend="beforeSend" />
                    </div>

                    <div class="statusbar">
                        <span v-if="processando" class="edicao-estado">Edição bloqueada enquanto o assistente processa a orientação</span>
                        <span v-else-if="alteracoesPendentes" class="edicao-estado is-pending">
                            <i></i>
                            Alterações manuais não salvas
                        </span>
                        <span v-else class="edicao-estado">Edição manual liberada · use “Salvar alterações” para guardar nesta interação</span>
                        <span class="ia-highlight-legend">
                            <i></i>
                            Marca-texto azul: indicador visual do conteúdo gerado e das alterações aplicadas pelo assistente; não será salvo no arquivo do artefato.
                        </span>
                        <div class="grow"></div>
                        <span>{{ mensagens.length }} {{ mensagens.length === 1 ? 'orientação' : 'orientações' }}</span>
                    </div>
                </main>

                <div class="resizer"
                     :class="{ dragging: redimensionando }"
                     v-show="chatAberto"
                     role="separator"
                     aria-orientation="vertical"
                     tabindex="0"
                     aria-label="Redimensionar painel do assistente"
                     @mousedown="iniciarResize"
                     @keydown="resizeTeclado"></div>

                <!-- ASSISTENTE -->
                <aside class="assistant" v-show="chatAberto" :style="{ width: larguraChat + 'px' }" aria-label="Assistente do documento">
                    <div class="ahead">
                        <span class="spark">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /></svg>
                        </span>
                        <h2>Assistente</h2>
                        <span v-if="processando" class="count">processando</span>
                        <div class="push"></div>
                        <button title="Ocultar assistente" aria-label="Ocultar assistente" @click="setChat(false)">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><path d="m9 6 6 6-6 6" /></svg>
                        </button>
                    </div>

                    <div v-if="mensagemProcessando" class="chat-status">
                        <span class="pulse"></span>{{ mensagemProcessando }}
                    </div>

                    <div class="thread" ref="chatBody">
                        <div v-if="!mensagens.length" class="intro">
                            <p class="lead">O que você deseja alterar no documento?</p>
                            <p>
                                Descreva a alteração em linguagem natural. O assistente localiza o trecho,
                                consulta a legislação quando necessário e prepara uma proposta para sua aprovação.
                            </p>
                            <div class="chips">
                                <button v-for="(chip, i) in sugestoes"
                                        :key="i"
                                        class="chip"
                                        :disabled="!podeEnviar"
                                        @click="usarSugestao(chip)">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /></svg>
                                    {{ chip }}
                                </button>
                            </div>
                        </div>

                        <div v-for="msg in mensagens"
                             :key="msg.idProcessoArtefatoInteracaoMensagem"
                             class="msg-group">
                            <div class="msg user">
                                <div class="bubble">{{ msg.dsMensagem }}</div>
                                <div class="stamp">{{ msg.dsLogin }} · {{ formatDate(msg.dtInclusao) }}</div>
                            </div>

                            <div class="msg bot">
                                <div class="who">
                                    <i>
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /></svg>
                                    </i>
                                    Assistente
                                </div>

                                <div v-if="msg.dsStatus === 'Processando'" class="text pending">
                                    <span class="typing"><i></i><i></i><i></i></span>
                                    Analisando o documento e a legislação…
                                </div>
                                <div v-else-if="msg.dsStatus === 'Erro' || msg.dsStatus === 'Cancelada'" class="callout error">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
                                    <span>{{ msg.dsErro || 'Não foi possível aplicar a alteração.' }}</span>
                                </div>
                                <div v-else-if="msg.dsStatus === 'Descartada'" class="callout neutral">
                                    <span>Proposta descartada. O documento não foi alterado.</span>
                                </div>
                                <div v-else-if="msg.tipoResposta === 'esclarecimento'" class="callout question">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><circle cx="12" cy="12" r="9" /><path d="M9.8 9a2.4 2.4 0 1 1 3.3 2.2c-.8.4-1.1.9-1.1 1.8M12 16h.01" /></svg>
                                    <div>
                                        <strong>Preciso de uma informação</strong>
                                        <span>{{ msg.dsResposta }}</span>
                                    </div>
                                </div>
                                <div v-else-if="msg.tipoResposta === 'informativo'" class="callout info">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
                                    <div>
                                        <strong>Conteúdo já contemplado</strong>
                                        <span>{{ msg.dsResposta }}</span>
                                        <ul v-if="evidenciasMensagem(msg).length" class="evidence-list">
                                            <li v-for="(ev, ei) in evidenciasMensagem(msg)" :key="ei">
                                                <b>{{ ev.fonte || 'Fonte' }}</b>: {{ ev.trecho }}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div v-else-if="msg.operacoes && msg.operacoes.length" class="proposal">
                                    <p v-if="msg.dsResposta" class="proposal-summary">{{ msg.dsResposta }}</p>
                                    <div v-if="evidenciasMensagem(msg).length" class="proposal-evidence">
                                        <strong>Base da sugestão</strong>
                                        <ul class="evidence-list">
                                            <li v-for="(ev, ei) in evidenciasMensagem(msg)" :key="ei">
                                                <b>{{ ev.fonte || 'Fonte' }}</b>: {{ ev.trecho }}
                                            </li>
                                        </ul>
                                    </div>

                                    <label v-for="op in msg.operacoes"
                                           :key="op.operacaoId"
                                           class="diff-card"
                                           :class="[
                                               `is-${(op.estado || 'Pendente').toLowerCase()}`,
                                               { selected: operacaoSelecionada(msg, op.operacaoId), 'is-sem-evidencia': op.semEvidencia }
                                           ]">
                                        <input v-if="msg.dsStatus === 'AguardandoAprovacao' && !op.semEvidencia"
                                               type="checkbox"
                                               :checked="operacaoSelecionada(msg, op.operacaoId)"
                                               @change="alternarOperacao(msg, op.operacaoId, $event.target.checked)" />
                                        <div class="diff-content">
                                            <header>
                                                <span class="diff-tag" :class="`tipo-${normalizarTipoOperacao(op.tipo)}`">
                                                    {{ tituloOperacao(op.tipo) }}
                                                </span>
                                                <span class="diff-location">{{ op.localizacao || op.alvoId || op.depoisDeId }}</span>
                                                <span v-if="op.semEvidencia" class="diff-badge sem-evidencia">Sem evidência</span>
                                                <span v-else-if="seloConfianca(op)" class="diff-badge" :class="seloConfianca(op).classe">{{ seloConfianca(op).texto }}</span>
                                                <span v-if="op.estado && op.estado !== 'Pendente'" class="diff-state">{{ op.estado }}</span>
                                            </header>

                                            <div v-if="op.orientacaoOrigem" class="diff-row anchor">
                                                <b>↳</b><span>Comando: {{ op.orientacaoOrigem }}</span>
                                            </div>
                                            <div v-if="textoAncora(op)" class="diff-row anchor">
                                                <b>↳</b><span>Depois de: {{ textoAncora(op) }}</span>
                                            </div>
                                            <div v-if="textoRemovido(op)" class="diff-row old">
                                                <b>−</b><span>{{ textoRemovido(op) }}</span>
                                            </div>
                                            <div v-if="op.conteudoNovo || op.conteudo" class="diff-row new">
                                                <b>+</b><span>{{ op.conteudoNovo || op.conteudo }}</span>
                                            </div>
                                            <div v-if="descricaoFormatacao(op)" class="diff-row format">
                                                <b>✎</b><span>{{ descricaoFormatacao(op) }}</span>
                                            </div>

                                            <div v-if="op.itensAfetados && op.itensAfetados.length > 1" class="affected">
                                                <strong>{{ op.itensAfetados.length }} itens serão afetados</strong>
                                                <ul>
                                                    <li v-for="item in op.itensAfetados.slice(0, 8)" :key="item.id">
                                                        <b v-if="item.numero">{{ item.numero }}</b> {{ item.texto }}
                                                    </li>
                                                </ul>
                                                <small v-if="op.itensAfetados.length > 8">
                                                    e mais {{ op.itensAfetados.length - 8 }} item(ns)
                                                </small>
                                            </div>

                                            <div v-for="(aviso, ai) in op.avisos || []" :key="ai" class="diff-warning">
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M12 3 2.5 20h19zM12 9v4M12 17h.01" /></svg>
                                                {{ aviso }}
                                            </div>
                                        </div>
                                    </label>

                                    <div v-if="msg.dsStatus === 'AguardandoAprovacao'" class="proposal-actions">
                                        <button class="mini danger"
                                                :disabled="acaoProposta"
                                                @click="descartarProposta(msg)">
                                            Descartar proposta
                                        </button>
                                        <button class="btn-primary"
                                                :disabled="acaoProposta || !quantidadeSelecionada(msg)"
                                                @click="aplicarProposta(msg)">
                                            Aplicar selecionadas ({{ quantidadeSelecionada(msg) }})
                                        </button>
                                    </div>
                                </div>
                                <div v-else-if="msg.dsStatus === 'AguardandoAprovacao'" class="callout error">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
                                    <div>
                                        <span>Não foi possível carregar as alterações desta proposta. Nada foi aplicado ao documento.</span>
                                        <div class="proposal-actions">
                                            <button class="mini danger"
                                                    :disabled="acaoProposta"
                                                    @click="descartarProposta(msg)">
                                                Descartar proposta
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div v-else-if="msg.dsResposta" class="text">{{ msg.dsResposta }}</div>
                                <div v-else-if="msg.dsStatus === 'Finalizada'" class="callout ok">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                    <span>Documento atualizado.</span>
                                </div>
                                <div v-else class="callout neutral">
                                    <span>Sem resposta registrada para esta orientação.</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="composer">
                        <div class="inputwrap" :class="{ disabled: !podeEnviar }">
                            <textarea ref="input"
                                      v-model="dsMensagem"
                                      rows="1"
                                      maxlength="5000"
                                      :disabled="!podeEnviar"
                                      aria-label="Mensagem para o assistente"
                                      placeholder="Peça uma alteração, ex.: “inclua a cláusula de garantia conforme a legislação”"
                                      @input="ajustarAltura"
                                      @keydown.enter.exact.prevent="enviar"></textarea>
                            <div class="crow">
                                <span class="counter" v-if="dsMensagem">{{ dsMensagem.length }}/5000</span>
                                <div class="grow"></div>
                                <button class="send"
                                        :disabled="!podeEnviar || !dsMensagem || dsMensagem.trim().length < 3"
                                        title="Enviar"
                                        aria-label="Enviar"
                                        @click="enviar">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
                                </button>
                            </div>
                        </div>
                        <p class="hint">
                            Nenhuma alteração é aplicada sem sua revisão. Edições feitas à mão são
                            salvas automaticamente antes de o assistente analisar o documento.
                        </p>
                    </div>
                </aside>
            </div>
        </div>

        <button class="reopen" v-show="!chatAberto && !errorMsg" @click="setChat(true)">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /></svg>
            Assistente
        </button>

        <div class="toast" :class="{ on: toast.visivel, erro: toast.tipo === 'erro' }">
            <svg v-if="toast.tipo === 'erro'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
            <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            <span>{{ toast.texto }}</span>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'
import Header from '@/components/Header.vue'
import { urlApi } from '@/config/global'
import { L10n, setCulture } from '@syncfusion/ej2-base'
import ptBR from '@syncfusion/ej2-locale/src/pt-BR.json'
import {
    DocumentEditorContainerComponent as EjsDocumenteditorcontainer,
    Toolbar,
    SpellChecker
} from '@syncfusion/ej2-vue-documenteditor'

L10n.load(ptBR)
setCulture('pt-BR')

export default {
    name: 'ProcessoArtefatoInteracao',
    components: {
        Header,
        'ejs-documenteditorcontainer': EjsDocumenteditorcontainer
    },
    provide: { DocumentEditorContainer: [Toolbar, SpellChecker] },
    props: {
        id: { type: [String, Number], required: true }
    },
    data() {
        return {
            urlApi,
            interacao: null,
            mensagens: [],
            dsMensagem: '',
            editorBusy: false,
            processando: false,
            mensagemProcessando: null,
            errorMsg: null,
            docErrorMsg: null,
            quantidadeDestaquesIa: 0,
            podeEnviar: true,
            chatAberto: true,
            larguraChat: 400,
            redimensionando: false,
            operacoesSelecionadas: {},
            acaoProposta: false,
            alteracoesPendentes: false,
            salvandoEdicao: false,
            toast: { visivel: false, texto: '', tipo: 'ok' },
            itensBarraFerramentas: [
                'Undo', 'Redo', 'Separator',
                'Image', 'Table', 'Hyperlink', 'Bookmark', 'TableOfContents', 'Separator',
                'Header', 'Footer', 'PageSetup', 'PageNumber', 'Break',
                'InsertFootnote', 'InsertEndnote', 'Separator',
                'Find', 'Separator',
                'Comments', 'TrackChanges', 'Separator',
                'FormFields', 'UpdateFields', 'ContentControl', 'XML Mapping'
            ],
            sugestoes: [
                'Identifique informações importantes que ainda faltam no documento',
                'Crie um item sobre garantia conforme a legislação aplicável',
                'Complemente o item 2.1 com uma justificativa mais detalhada',
                'Remova o item 4.3 e todos os seus subitens'
            ]
        }
    },
    computed: {
        hubConection() {
            return this.$store.state.hubConection
        },
        statusTexto() {
            if (this.errorMsg) return 'Indisponível'
            if (this.processando) return 'Processando alteração'
            if (this.salvandoEdicao) return 'Salvando alterações'
            if (this.alteracoesPendentes) return 'Alterações não salvas'
            if (this.mensagens.some(msg => msg.dsStatus === 'AguardandoAprovacao')) return 'Aguardando sua revisão'
            if (this.editorBusy) return 'Carregando documento'
            return 'Pronto para editar'
        },
        statusDotClass() {
            if (this.errorMsg) return 'is-error'
            if (this.processando || this.editorBusy || this.salvandoEdicao) return 'is-busy'
            if (this.alteracoesPendentes) return 'is-pending'
            return 'is-ok'
        }
    },
    watch: {
        hubConection(conexao) {
            this.unbindSignalR()
            if (conexao) this.bindSignalR()
        },
        processando(ativo) {
            if (ativo) this.iniciarMonitoramento()
            else this.pararMonitoramento()
            this.aplicarPermissaoEdicao()
        }
    },
    created() {
        this.editor = null
        this._urlEmAbertura = null
        this._monitorTimer = null
        this._revalidando = false
        this._ignorarMudancas = false
        this._paginaVisivel = 0
        this._restaurarPaginaTimer = null
        this._historicoTimer = null
        this._historicoEstado = null
    },
    mounted() {
        this.iniciar()
        this.bindSignalR()
        window.addEventListener('mousemove', this.aoMoverResize)
        window.addEventListener('mouseup', this.finalizarResize)
        window.addEventListener('beforeunload', this.avisarSaidaComPendencias)
        document.addEventListener('visibilitychange', this.aoVoltarParaAba)
        if (window.innerWidth < 1100) this.chatAberto = false
    },
    beforeDestroy() {
        this.unbindSignalR()
        this.pararMonitoramento()
        this.pararVigilanciaHistorico()
        window.removeEventListener('mousemove', this.aoMoverResize)
        window.removeEventListener('mouseup', this.finalizarResize)
        window.removeEventListener('beforeunload', this.avisarSaidaComPendencias)
        document.removeEventListener('visibilitychange', this.aoVoltarParaAba)
        clearTimeout(this._toastTimer)
        clearTimeout(this._liberarMudancas)
        clearTimeout(this._restaurarPaginaTimer)
    },
    methods: {
        formatDate(dt) {
            if (!dt) return ''
            try {
                return new Date(dt).toLocaleString('pt-BR')
            } catch {
                return dt
            }
        },
        mostrarToast(texto, tipo = 'ok') {
            this.toast = { visivel: true, texto, tipo }
            clearTimeout(this._toastTimer)
            this._toastTimer = setTimeout(() => { this.toast.visivel = false }, 4200)
        },
        setChat(aberto) {
            this.chatAberto = aberto
            if (aberto) this.$nextTick(() => this.$refs.input?.focus())
        },
        async voltar() {
            if (this.alteracoesPendentes && !(await this.confirmarSaidaComPendencias())) return
            if (this.interacao?.idProcesso) {
                this.$router.push({ name: 'DetalhesProcesso', params: { id: this.interacao.idProcesso } })
            } else {
                this.$router.back()
            }
        },
        iniciarResize(e) {
            this.redimensionando = true
            document.body.style.cursor = 'col-resize'
            e.preventDefault()
        },
        aoMoverResize(e) {
            if (!this.redimensionando) return
            this.larguraChat = Math.min(620, Math.max(330, window.innerWidth - e.clientX))
        },
        finalizarResize() {
            if (!this.redimensionando) return
            this.redimensionando = false
            document.body.style.cursor = ''
        },
        resizeTeclado(e) {
            if (e.key === 'ArrowLeft') this.larguraChat = Math.min(620, this.larguraChat + 24)
            if (e.key === 'ArrowRight') this.larguraChat = Math.max(330, this.larguraChat - 24)
        },
        ajustarAltura() {
            const el = this.$refs.input
            if (!el) return
            el.style.height = 'auto'
            el.style.height = Math.min(140, el.scrollHeight) + 'px'
        },
        usarSugestao(texto) {
            this.dsMensagem = texto
            this.$nextTick(() => {
                this.ajustarAltura()
                this.$refs.input?.focus()
            })
        },
        beforeSend(args) {
            const raw = this.$store?.state?.authToken || localStorage.getItem('authToken')
            const auth = raw?.startsWith('Bearer ') ? raw : (raw ? `Bearer ${raw}` : '')
            if (auth) args.headers = [{ Authorization: auth }]
        },
        onEditorCreated() {
            this.editor = this.resolverEditor()
            const url = this.resolverUrlDocumento()
            if (url) this.abrirDocumento(url)
        },
        resolverEditor() {
            if (this.editor) return this.editor
            const instancia = this.$refs.docx?.ej2Instances?.documentEditor || null
            if (instancia) this.observarEditor(instancia)
            return instancia
        },
        observarEditor(editor) {
            if (!editor || editor._sollaiObservado) return
            editor._sollaiObservado = true

            // addEventListener em vez de atribuir a propriedade: atribuir descarta o
            // handler que o container registrou no mesmo evento, e é ele que reavalia
            // a barra de ferramentas (inclusive Desfazer/Refazer) e o contador de páginas.

            // Qualquer digitação, colagem ou formatação feita na tela precisa ser
            // gravada antes de o assistente ler o documento, senão ele trabalharia
            // sobre a versão anterior e devolveria a alteração do usuário desfeita.
            editor.addEventListener('contentChange', () => {
                if (this._ignorarMudancas) return
                this.alteracoesPendentes = true
            })

            // Rolar a tela não move a seleção, então este é o único evento que informa
            // a página que o usuário está lendo — é ela que precisa ser restaurada
            // quando o documento é recarregado.
            editor.addEventListener('viewChange', args => {
                const pagina = Number(args?.startPage) || 0
                if (pagina > 0) this._paginaVisivel = pagina
            })

            this.sincronizarHistorico(editor)
            this.iniciarVigilanciaHistorico()
        },

        // Desfazer e refazer não disparam contentChange nesta versão do Syncfusion.
        // Sem acompanhar as pilhas de histórico, um Ctrl+Z depois de salvar mudaria o
        // documento sem reabilitar o botão de salvar.
        estadoHistorico(editor) {
            try {
                const historico = editor?.editorHistoryModule || editor?.editorHistory
                const desfazer = historico?.undoStack || []
                const refazer = historico?.redoStack || []
                return {
                    assinatura: `${desfazer.length}|${refazer.length}`,
                    topo: desfazer.length ? desfazer[desfazer.length - 1] : null
                }
            } catch {
                return { assinatura: '', topo: null }
            }
        },
        sincronizarHistorico(editor) {
            this._historicoEstado = this.estadoHistorico(editor || this.editor)
        },
        iniciarVigilanciaHistorico() {
            if (this._historicoTimer) return
            this._historicoTimer = setInterval(this.avaliarHistorico, 500)
        },
        pararVigilanciaHistorico() {
            if (!this._historicoTimer) return
            clearInterval(this._historicoTimer)
            this._historicoTimer = null
        },
        // Só marca pendência: limpar aqui desfaria o aviso de digitação ainda não
        // registrada no histórico. O estado volta a limpo ao salvar ou ao recarregar.
        avaliarHistorico() {
            const editor = this.editor
            if (!editor) return
            const atual = this.estadoHistorico(editor)
            if (this._ignorarMudancas) {
                this._historicoEstado = atual
                return
            }
            const anterior = this._historicoEstado || { assinatura: '', topo: null }
            if (atual.assinatura !== anterior.assinatura || atual.topo !== anterior.topo) {
                this._historicoEstado = atual
                this.alteracoesPendentes = true
            }
        },
        aplicarPermissaoEdicao() {
            const editor = this.resolverEditor()
            if (!editor) return
            editor.isReadOnly = this.processando
        },

        paginaEmExibicao() {
            const editor = this.editor
            if (!editor) return 0
            const selecao = editor.selectionModule || editor.selection
            return this._paginaVisivel || Number(selecao?.startPage) || 0
        },
        // A paginação termina depois do open, e sem ela o scrollToPage não encontra a
        // página. O número é limitado ao total porque o documento pode ter encurtado.
        restaurarPagina(editor, pagina) {
            clearTimeout(this._restaurarPaginaTimer)
            if (!pagina || pagina <= 1) return

            let tentativas = 0
            const tentar = () => {
                const total = Number(editor.pageCount) || 0
                if (!total) {
                    if (tentativas++ >= 20) return
                    this._restaurarPaginaTimer = setTimeout(tentar, 100)
                    return
                }
                editor.scrollToPage(Math.min(pagina, total))
            }
            this._restaurarPaginaTimer = setTimeout(tentar, 0)
        },

        async aguardarEditor() {
            for (let tentativa = 0; tentativa < 40; tentativa++) {
                this.editor = this.resolverEditor()
                if (this.editor) return this.editor
                await new Promise(resolve => setTimeout(resolve, 150))
            }
            return null
        },
        resolverUrlDocumento() {
            const i = this.interacao || {}
            return (
                i.urlArquivoEditado ||
                i.urlArquivo ||
                i.UrlArquivoEditado ||
                i.UrlArquivo ||
                ''
            ).trim()
        },
        mensagemErroAxios(e, fallback) {
            const data = e?.response?.data
            if (typeof data === 'string' && data.trim()) return data
            if (data?.msg) return data.msg
            if (Array.isArray(data?.errors) && data.errors[0]) return data.errors[0]
            return e?.message || fallback
        },
        async iniciar() {
            this.errorMsg = null
            this.docErrorMsg = null
            try {
                const { data } = await axios.post(`${urlApi}/ProcessoArtefatoInteracao/Abrir`, {
                    idProcessoArtefato: Number(this.id)
                })
                if (!data?.success) {
                    this.errorMsg = data?.msg || data?.errors?.[0] || 'Erro ao abrir interação.'
                    return
                }
                this.interacao = data.data
                if (!this.resolverUrlDocumento()) {
                    this.errorMsg = 'Interação aberta, mas o documento não possui URL de arquivo.'
                    return
                }
                await this.carregarMensagens()
                await this.verificarBloqueio()
                await this.abrirDocumento(this.resolverUrlDocumento())
            } catch (e) {
                this.errorMsg = this.mensagemErroAxios(e, 'Erro ao abrir interação.')
            }
        },
        async recarregarInteracao() {
            const { data } = await axios.post(
                `${urlApi}/ProcessoArtefatoInteracao/Abrir`,
                { idProcessoArtefato: Number(this.id) },
                { showLoading: false }
            )
            if (!data?.success || !data.data) return
            const urlAnterior = this.resolverUrlDocumento()
            this.interacao = data.data
            const url = this.resolverUrlDocumento()
            if (url && url !== urlAnterior) await this.abrirDocumento(url)
        },
        async abrirDocumento(url) {
            if (!url) {
                this.docErrorMsg = 'URL do documento vazia.'
                return
            }
            // O evento created e o carregamento da interação podem pedir a mesma abertura.
            if (this._urlEmAbertura === url) return
            this._urlEmAbertura = url
            this.editorBusy = true
            this.docErrorMsg = null
            try {
                const editor = await this.aguardarEditor()
                if (!editor) {
                    this.docErrorMsg = 'O editor de documentos não ficou pronto. Use Recarregar para tentar novamente.'
                    return
                }
                const resposta = await axios.post(
                    `${urlApi}/DOCXEditor/ImportFromUrlRevisaoIa`,
                    {
                        url,
                        Url: url,
                        idProcessoArtefatoInteracao:
                            this.interacao?.idProcessoArtefatoInteracao
                    },
                    { showLoading: false }
                )
                const sfdt = resposta.data
                this.quantidadeDestaquesIa = Number(
                    resposta.headers?.['x-sollai-destaques-ia'] || 0
                )
                // Recarregar joga a vista para a primeira página. A posição de leitura
                // é lida antes do open porque o próprio open a descarta.
                const paginaAnterior = this.paginaEmExibicao()

                // A carga do documento dispara os mesmos eventos de uma digitação.
                this._ignorarMudancas = true
                editor.open(sfdt)
                editor.isReadOnly = this.processando
                this.alteracoesPendentes = false
                this.sincronizarHistorico(editor)
                this.restaurarPagina(editor, paginaAnterior)
                clearTimeout(this._liberarMudancas)
                this._liberarMudancas = setTimeout(() => {
                    this._ignorarMudancas = false
                }, 600)
            } catch (e) {
                this._ignorarMudancas = false
                this.quantidadeDestaquesIa = 0
                this.docErrorMsg = this.mensagemErroAxios(e, 'Erro ao carregar documento no editor.')
            } finally {
                this._urlEmAbertura = null
                this.editorBusy = false
            }
        },
        async carregarMensagens() {
            if (!this.interacao?.idProcessoArtefatoInteracao) return
            const { data } = await axios.get(
                `${urlApi}/ProcessoArtefatoInteracao/Mensagens/${this.interacao.idProcessoArtefatoInteracao}`,
                { showLoading: false }
            )
            if (data?.success) {
                this.mensagens = data.data || []
                this.mensagens.forEach(msg => {
                    if (msg.dsStatus === 'AguardandoAprovacao' &&
                        !this.operacoesSelecionadas[msg.idProcessoArtefatoInteracaoMensagem]) {
                        this.$set(
                            this.operacoesSelecionadas,
                            msg.idProcessoArtefatoInteracaoMensagem,
                            // Operações sem evidência entram apenas para revisão: não têm
                            // texto aplicável, então não são pré-selecionadas.
                            (msg.operacoes || [])
                                .filter(op => !op.semEvidencia)
                                .map(op => op.operacaoId)
                        )
                    }
                })
                this.$nextTick(() => {
                    const el = this.$refs.chatBody
                    if (el) el.scrollTop = el.scrollHeight
                })
            }
        },
        async verificarBloqueio() {
            if (!this.interacao?.idProcessoArtefatoInteracao) return
            const { data } = await axios.get(`${urlApi}/ProcessoArtefatoInteracao/VerificaRealizarMensagem`, {
                params: { idProcessoArtefatoInteracao: this.interacao.idProcessoArtefatoInteracao },
                showLoading: false
            })
            this.podeEnviar = !!data?.data
            this.processando = this.mensagens.some(msg => msg.dsStatus === 'Processando')
        },
  
        iniciarMonitoramento() {
            if (this._monitorTimer) return
            this._monitorTimer = setInterval(this.revalidarEstado, 12000)
        },
        pararMonitoramento() {
            if (!this._monitorTimer) return
            clearInterval(this._monitorTimer)
            this._monitorTimer = null
        },
        assinaturaMensagens() {
            return this.mensagens
                .map(msg => `${msg.idProcessoArtefatoInteracaoMensagem}:${msg.dsStatus}`)
                .join('|')
        },
        async revalidarEstado() {
            if (this._revalidando || !this.interacao?.idProcessoArtefatoInteracao) return
            this._revalidando = true
            try {
                const assinaturaAnterior = this.assinaturaMensagens()
                const processandoAntes = this.processando
                await this.carregarMensagens()
                await this.verificarBloqueio()
                if (this.assinaturaMensagens() === assinaturaAnterior) return

                this.mensagemProcessando = null
                await this.recarregarInteracao()
                if (processandoAntes && !this.processando) {
                    this.mostrarToast('Resposta do assistente recebida.')
                }
            } catch {
                // Rede instável.
            } finally {
                this._revalidando = false
            }
        },
        aoVoltarParaAba() {
            if (document.visibilityState === 'visible' && this.processando) {
                this.revalidarEstado()
            }
        },
        // Grava no S3 o documento como está no editor e aponta a interação para ele.
        // O artefato só recebe essa versão quando o usuário usa "Salvar no artefato".
        async salvarEdicoesManuais(automatico = false) {
            if (!this.interacao?.idProcessoArtefatoInteracao || this.salvandoEdicao) return false

            const editor = await this.aguardarEditor()
            if (!editor) {
                this.mostrarToast('O editor não está disponível para salvar as alterações.', 'erro')
                return false
            }

            this.salvandoEdicao = true
            try {
                const conteudo = editor.serialize()
                // Marca o ponto correspondente ao que foi enviado: uma edição feita
                // durante a requisição fica pendente na próxima verificação.
                const estadoEnviado = this.estadoHistorico(editor)
                const { data } = await axios.post(
                    `${urlApi}/ProcessoArtefatoInteracao/SalvarEdicaoManual`,
                    {
                        idProcessoArtefatoInteracao: this.interacao.idProcessoArtefatoInteracao,
                        conteudo
                    },
                    { showLoading: false }
                )
                if (!data?.success) throw new Error(data?.msg || 'Não foi possível salvar as alterações.')
                if (data.data?.urlArquivoEditado) {
                    // Mantém a URL local igual à do servidor para a revalidação não
                    // recarregar o documento e jogar o usuário para o início da página.
                    this.interacao.urlArquivoEditado = data.data.urlArquivoEditado
                }
                // O histórico continua disponível depois de salvar: se o usuário
                // desfizer algo, a comparação com este ponto reabilita o salvamento.
                this._historicoEstado = estadoEnviado
                this.alteracoesPendentes = false
                this.mostrarToast(this.mensagemSalvamento(automatico))
                return true
            } catch (e) {
                this.mostrarToast(this.mensagemErroAxios(e, 'Erro ao salvar as alterações.'), 'erro')
                return false
            } finally {
                this.salvandoEdicao = false
            }
        },
        // Editar o documento move os parágrafos de lugar. Uma proposta já gerada é
        // conferida contra o texto original na hora de aplicar, então ela pode ter de
        // ser refeita: o aviso evita que a recusa apareça sem explicação.
        mensagemSalvamento(automatico) {
            const propostaPendente = this.mensagens.some(
                msg => msg.dsStatus === 'AguardandoAprovacao'
            )
            if (propostaPendente) {
                return 'Alterações salvas. Se a proposta pendente não puder ser aplicada, envie a orientação novamente.'
            }
            return automatico
                ? 'Suas alterações foram salvas antes de acionar o assistente.'
                : 'Alterações salvas nesta interação.'
        },
        async garantirEdicoesSalvas() {
            if (!this.alteracoesPendentes) return true
            return await this.salvarEdicoesManuais(true)
        },
        avisarSaidaComPendencias(evento) {
            if (!this.alteracoesPendentes) return
            evento.preventDefault()
            evento.returnValue = ''
        },
        async confirmarSaidaComPendencias() {
            const { isConfirmed } = await Swal.fire({
                icon: 'warning',
                title: 'Sair sem salvar?',
                text: 'Você editou o documento e ainda não salvou. As alterações serão perdidas.',
                showCancelButton: true,
                confirmButtonText: 'Sair sem salvar',
                cancelButtonText: 'Continuar editando',
                confirmButtonColor: '#d33',
                cancelButtonColor: '#6b7280',
                reverseButtons: true
            })
            return isConfirmed
        },
        async enviar() {
            const texto = (this.dsMensagem || '').trim()
            if (!this.podeEnviar || texto.length < 3) return
            // O assistente lê o arquivo gravado, e não o que está na tela.
            if (!(await this.garantirEdicoesSalvas())) return
            this.processando = true
            this.podeEnviar = false
            this.mensagemProcessando = 'Enviando orientação…'
            try {
                const { data } = await axios.post(
                    `${urlApi}/ProcessoArtefatoInteracao/PostMensagem`,
                    {
                        idProcessoArtefatoInteracao: this.interacao.idProcessoArtefatoInteracao,
                        dsMensagem: texto
                    },
                    { showLoading: false }
                )
                if (!data?.success) {
                    throw new Error(data?.msg || 'Falha ao enviar')
                }
                this.dsMensagem = ''
                this.$nextTick(this.ajustarAltura)
                await this.carregarMensagens()
            } catch (e) {
                this.mensagemProcessando = null
                this.podeEnviar = true
                this.processando = false
                this.mostrarToast(this.mensagemErroAxios(e, 'Erro ao enviar mensagem.'), 'erro')
            }
        },
        normalizarTipoOperacao(tipo) {
            return (tipo || '').replace(/[\s_-]/g, '').toLowerCase()
        },
        tituloOperacao(tipo) {
            const valor = this.normalizarTipoOperacao(tipo)
            if (valor === 'removersubarvore' || valor === 'remover') return 'Remover'
            if (valor === 'removerparagrafo') return 'Remover duplicata'
            if (valor === 'removertabela') return 'Remover tabela'
            if (valor === 'removerimagem') return 'Remover imagem'
            if (valor === 'substituir') return 'Substituir'
            if (valor === 'inserir') return 'Inserir'
            if (valor === 'formatar') return 'Formatar'
            return tipo || 'Alteração'
        },
        descricaoFormatacao(op) {
            if (this.normalizarTipoOperacao(op.tipo) !== 'formatar') return ''
            const f = op.formatacao || {}
            if (f.descricao) {
                const qtd = (op.itensAfetados || []).length
                return qtd > 1
                    ? `${f.descricao} · ${qtd} parágrafo(s)`
                    : f.descricao
            }
            const partes = []
            if (f.bold === true) partes.push('Aplicar negrito')
            if (f.bold === false) partes.push('Remover negrito')
            if (f.cor) partes.push(`Cor #${f.cor}`)
            if (f.destaque === 'none') partes.push('Remover realce')
            else if (f.destaque) partes.push(`Realce ${f.destaque}`)
            if (f.fundo === 'none') partes.push('Remover fundo')
            else if (f.fundo) partes.push(`Fundo #${f.fundo}`)
            return partes.join(' · ')
        },
        evidenciasMensagem(msg) {
            const evidencias = msg?.diagnostico?.evidencias || []
            return evidencias
                .filter(ev => ev && (ev.fonte || ev.trecho))
                .slice(0, 8)
        },
        textoRemovido(op) {
            const tipo = this.normalizarTipoOperacao(op.tipo)
            if (tipo === 'inserir' || tipo === 'formatar') return ''
            return op.conteudoAnterior || ''
        },
        textoAncora(op) {
            const tipo = this.normalizarTipoOperacao(op.tipo)
            if (tipo === 'inserir' || tipo === 'removertabela' || tipo === 'removerimagem') {
                return op.conteudoAncora || ''
            }
            return ''
        },
        // Só sinaliza quando a IA reportou confiança baixa em uma correspondência de lote;
        // operações determinísticas vêm sem confianca e não exibem selo.
        seloConfianca(op) {
            if (op.confianca == null) return null
            if (op.confianca <= 0.5) return { texto: 'Baixa confiança', classe: 'baixa' }
            return null
        },
        operacaoSelecionada(msg, operacaoId) {
            const selecionadas = this.operacoesSelecionadas[msg.idProcessoArtefatoInteracaoMensagem] || []
            return selecionadas.includes(operacaoId)
        },
        alternarOperacao(msg, operacaoId, selecionada) {
            const id = msg.idProcessoArtefatoInteracaoMensagem
            const atuais = [...(this.operacoesSelecionadas[id] || [])]
            const indice = atuais.indexOf(operacaoId)
            if (selecionada && indice < 0) atuais.push(operacaoId)
            if (!selecionada && indice >= 0) atuais.splice(indice, 1)
            this.$set(this.operacoesSelecionadas, id, atuais)
        },
        quantidadeSelecionada(msg) {
            return (this.operacoesSelecionadas[msg.idProcessoArtefatoInteracaoMensagem] || []).length
        },
        escaparHtml(texto) {
            const elemento = document.createElement('div')
            elemento.textContent = texto == null ? '' : String(texto)
            return elemento.innerHTML
        },
        // Lista o que será aplicado para que a confirmação não seja apenas um número.
        resumoOperacoes(msg, operacoesIds) {
            const selecionadas = (msg.operacoes || [])
                .filter(op => operacoesIds.includes(op.operacaoId))
            const itens = selecionadas.slice(0, 8).map(op => {
                const titulo = this.escaparHtml(this.tituloOperacao(op.tipo))
                const onde = op.localizacao ? ` — ${this.escaparHtml(op.localizacao)}` : ''
                return `<li><b>${titulo}</b>${onde}</li>`
            })
            const restantes = selecionadas.length - itens.length
            if (restantes > 0) itens.push(`<li>e mais ${restantes} alteração(ões)</li>`)
            return itens.join('')
        },
        quantidadeItensAfetados(msg, operacoesIds) {
            const selecionadas = (msg.operacoes || [])
                .filter(op => operacoesIds.includes(op.operacaoId))
            const itens = new Set()
            selecionadas.forEach(op => {
                const afetados = (op.itensAfetados || []).filter(item => item && item.id)
                if (afetados.length) {
                    afetados.forEach(item => itens.add(item.id))
                    return
                }
                // Inserções e objetos estruturais podem não trazer itensAfetados.
                itens.add(op.alvoId || op.depoisDeId || op.operacaoId)
            })
            return itens.size
        },
        async aplicarProposta(msg) {
            const operacoesIds = this.operacoesSelecionadas[msg.idProcessoArtefatoInteracaoMensagem] || []
            if (!operacoesIds.length) return
            const itensAfetados = this.quantidadeItensAfetados(msg, operacoesIds)

            const { isConfirmed } = await Swal.fire({
                icon: 'warning',
                title: operacoesIds.length === 1
                    ? 'Aplicar a alteração selecionada?'
                    : `Aplicar as ${operacoesIds.length} alterações selecionadas?`,
                html: `<div style="text-align:left;line-height:1.5;">
                    <p style="margin:0 0 8px;"><b>Impacto: ${itensAfetados} item(ns) do documento.</b></p>
                    <ul style="margin:8px 0 0 18px;padding:0;max-height:220px;overflow:auto;">${this.resumoOperacoes(msg, operacoesIds)}</ul>
                    <div style="margin-top:14px;padding:12px;border:1px solid #f59e0b;border-radius:8px;background:#fffbeb;color:#92400e;">
                        <b>Atenção: esta ação reinicia o histórico de desfazer.</b><br>
                        O documento atual será salvo, as alterações serão aplicadas e o arquivo será recarregado.
                        Depois disso, não será possível usar Ctrl+Z/Ctrl+Y para desfazer ou refazer alterações realizadas antes desta aplicação.
                    </div>
                </div>`,
                showCancelButton: true,
                confirmButtonText: 'Salvar e aplicar alterações',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#2563eb',
                cancelButtonColor: '#6b7280',
                reverseButtons: true
            })
            if (!isConfirmed) return

            this.acaoProposta = true
            try {
                // A proposta é aplicada sobre o arquivo gravado: sem salvar antes, a
                // edição manual seria descartada junto.
                if (!(await this.garantirEdicoesSalvas())) return
                const { data } = await axios.post(
                    `${urlApi}/ProcessoArtefatoInteracao/AplicarProposta/${msg.idProcessoArtefatoInteracaoMensagem}`,
                    { operacoesIds },
                    { showLoading: false }
                )
                if (!data?.success) throw new Error(data?.msg || 'Não foi possível aplicar a proposta.')
                if (data.data?.urlArquivoEditado && this.interacao) {
                    this.interacao.urlArquivoEditado = data.data.urlArquivoEditado
                }
                await this.carregarMensagens()
                await this.verificarBloqueio()
                await this.abrirDocumento(this.resolverUrlDocumento())
                this.mostrarToast('Alterações selecionadas aplicadas ao documento.')
            } catch (e) {
                this.mostrarToast(this.mensagemErroAxios(e, 'Erro ao aplicar proposta.'), 'erro')
            } finally {
                this.acaoProposta = false
            }
        },
        async descartarProposta(msg) {
            const { isConfirmed } = await Swal.fire({
                icon: 'warning',
                title: 'Descartar toda a proposta?',
                text: 'Nenhuma alteração será aplicada e o documento permanecerá como está.',
                showCancelButton: true,
                confirmButtonText: 'Sim, descartar',
                cancelButtonText: 'Voltar',
                confirmButtonColor: '#d33',
                cancelButtonColor: '#6b7280',
                reverseButtons: true
            })
            if (!isConfirmed) return

            this.acaoProposta = true
            try {
                const { data } = await axios.post(
                    `${urlApi}/ProcessoArtefatoInteracao/DescartarProposta/${msg.idProcessoArtefatoInteracaoMensagem}`,
                    null,
                    { showLoading: false }
                )
                if (!data?.success) throw new Error(data?.msg || 'Não foi possível descartar a proposta.')
                await this.carregarMensagens()
                await this.verificarBloqueio()
                this.mostrarToast('Proposta descartada.')
            } catch (e) {
                this.mostrarToast(this.mensagemErroAxios(e, 'Erro ao descartar proposta.'), 'erro')
            } finally {
                this.acaoProposta = false
            }
        },
        async finalizar() {
            if (!this.interacao?.idProcessoArtefatoInteracao) return
            if (!(await this.garantirEdicoesSalvas())) return
            try {
                const { data } = await axios.post(
                    `${urlApi}/ProcessoArtefatoInteracao/Finalizar/${this.interacao.idProcessoArtefatoInteracao}`
                )
                if (data?.success) {
                    this.mostrarToast('Versão salva no artefato com sucesso.')
                    setTimeout(this.voltar, 900)
                } else {
                    this.mostrarToast(data?.msg || 'Não foi possível salvar.', 'erro')
                }
            } catch (e) {
                this.mostrarToast(this.mensagemErroAxios(e, 'Erro ao finalizar.'), 'erro')
            }
        },
        eventoDestaInteracao(idMensagem) {
            return this.mensagens.some(
                msg => msg.idProcessoArtefatoInteracaoMensagem === idMensagem
            )
        },
        bindSignalR() {
            if (!this.hubConection || this._onNotif) return
            this._onStatus = (idMsg, status) => {
                if (!this.processando && !this.eventoDestaInteracao(idMsg)) return
                this.mensagemProcessando = status
            }
            this._onNotif = async (idMsg, status, dt, resumo, url) => {
                if (!this.processando && !this.eventoDestaInteracao(idMsg)) return
                this.mensagemProcessando = null
                await this.carregarMensagens()
                await this.verificarBloqueio()
                if (!this.eventoDestaInteracao(idMsg)) return
                if (status === 'Finalizada') {
                    if (url) {
                        if (this.interacao) this.interacao.urlArquivoEditado = url
                        await this.abrirDocumento(this.resolverUrlDocumento())
                        this.mostrarToast(resumo || 'Documento atualizado.')
                    } else {
                        this.mostrarToast(resumo || 'Análise concluída.')
                    }
                } else if (status === 'Erro') {
                    this.mostrarToast(resumo || 'Não foi possível aplicar a alteração.', 'erro')
                } else if (status === 'AguardandoAprovacao') {
                    this.mostrarToast(resumo || 'Proposta pronta para revisão.')
                } else if (status === 'Descartada') {
                    this.mostrarToast('Proposta descartada.')
                }
            }
            this._onRealizar = (fl) => {
                this.podeEnviar = fl === 'Sim'
                this.processando = this.mensagens.some(msg => msg.dsStatus === 'Processando')
            }
            this.hubConection.on('AtualizaStatusProcessoArtefatoInteracao', this._onStatus)
            this.hubConection.on('RecebeNotificacaoProcessoArtefatoInteracao', this._onNotif)
            this.hubConection.on('AtualizaRealizarProcessoArtefatoInteracao', this._onRealizar)
        },
        unbindSignalR() {
            if (this.hubConection) {
                if (this._onStatus) this.hubConection.off('AtualizaStatusProcessoArtefatoInteracao', this._onStatus)
                if (this._onNotif) this.hubConection.off('RecebeNotificacaoProcessoArtefatoInteracao', this._onNotif)
                if (this._onRealizar) this.hubConection.off('AtualizaRealizarProcessoArtefatoInteracao', this._onRealizar)
            }
            this._onStatus = null
            this._onNotif = null
            this._onRealizar = null
        }
    }
}
</script>

<style scoped>
    .ia-root {
        --ink: #0f172a;
        --ink-2: #334155;
        --ink-3: #64748b;
        --ink-4: #94a3b8;
        --line: #e2e8f0;
        --line-soft: #eef2f6;
        --paper: #ffffff;
        --chrome: #f8fafc;
        --canvas: #eef1f5;
        --brand: #2563eb;
        --brand-700: #1d4ed8;
        --brand-50: #eff6ff;
        --ok: #15803d;
        --ok-50: #ecfdf5;
        --danger: #b91c1c;
        --danger-50: #fef2f2;
        --danger-line: #fecaca;
        --r-sm: 6px;
        --r-md: 10px;
        --r-lg: 14px;
        --shadow-1: 0 1px 2px rgba(15,23,42,.06), 0 1px 1px rgba(15,23,42,.04);
        --shadow-2: 0 8px 24px -8px rgba(15,23,42,.18), 0 2px 6px rgba(15,23,42,.06);
        --topbar: 54px;
    }

    /* O .tyn-appbar é fixed; só o .tyn-content recebe o margin-top do tema */
    .ia-app {
        display: flex;
        flex-direction: column;
        margin-top: var(--appbar-height);
        height: calc(100vh - var(--appbar-height));
        height: calc(100dvh - var(--appbar-height));
        background: var(--canvas);
        color: var(--ink);
        font-size: 13px;
        overflow: hidden;
    }

    .ia-app button {
        font: inherit;
        color: inherit;
        background: none;
        border: 0;
        cursor: pointer;
    }

    /* TOPO */
    .ia-topbar {
        height: var(--topbar);
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 0 14px;
        background: var(--paper);
        border-bottom: 1px solid var(--line);
        z-index: 20;
    }

    .doc-id {
        min-width: 0;
        line-height: 1.2;
    }

        .doc-id h1 {
            margin: 0;
            font-size: 14px;
            font-weight: 600;
            letter-spacing: -.005em;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .doc-id .meta {
            display: flex;
            align-items: center;
            gap: 7px;
            font-size: 11.5px;
            color: var(--ink-3);
            margin-top: 2px;
        }

        .doc-id .meta .sep {
            color: var(--line);
        }

    .dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        flex: 0 0 auto;
        background: var(--brand);
    }

        .dot.is-ok {
            background: var(--ok);
        }

        .dot.is-busy {
            background: var(--brand);
            animation: pulse 1.2s ease-in-out infinite;
        }

        .dot.is-error {
            background: var(--danger);
        }

        .dot.is-pending {
            background: #f59e0b;
        }

    @keyframes pulse {
        50% {
            opacity: .3;
        }
    }

    .spacer {
        flex: 1;
    }

    .tbtn {
        height: 32px;
        padding: 0 11px;
        border-radius: var(--r-sm);
        color: var(--ink-2);
        display: inline-flex;
        align-items: center;
        gap: 7px;
        font-size: 12.5px;
        font-weight: 500;
    }

        .tbtn:hover {
            background: var(--chrome);
            color: var(--ink);
        }

        .tbtn.icon {
            width: 32px;
            padding: 0;
            justify-content: center;
        }

        .tbtn:disabled {
            opacity: .45;
            cursor: not-allowed;
        }

        .tbtn.has-pending {
            color: var(--brand-700);
            background: var(--brand-50);
            box-shadow: inset 0 0 0 1px #bfdbfe;
        }

            .tbtn.has-pending:hover {
                background: #dbeafe;
                color: var(--brand-700);
            }

    .btn-primary {
        height: 32px;
        padding: 0 15px;
        border-radius: var(--r-sm);
        background: var(--brand);
        color: #fff;
        font-size: 12.5px;
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        gap: 7px;
        box-shadow: var(--shadow-1);
    }

        .btn-primary:hover:not(:disabled) {
            background: var(--brand-700);
        }

        .btn-primary:disabled {
            opacity: .5;
            cursor: not-allowed;
        }

    .mini {
        height: 28px;
        padding: 0 11px;
        border-radius: var(--r-sm);
        font-size: 12px;
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border: 1px solid var(--line);
        color: var(--ink-2);
        background: var(--paper);
    }

        .mini:hover {
            background: var(--chrome);
        }

    /* CORPO */
    .ia-body {
        flex: 1;
        display: flex;
        min-height: 0;
    }

    .editor-pane {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-width: 0;
        min-height: 0;
        background: var(--paper);
    }

    .editor-host {
        position: relative;
        flex: 1;
        min-height: 0;
    }

    .doc-error {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 9px 14px;
        background: var(--danger-50);
        border-bottom: 1px solid var(--danger-line);
        color: var(--danger);
        font-size: 12.5px;
    }

    .doc-error-text {
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }

    .editor-overlay {
        position: absolute;
        inset: 0;
        z-index: 5;
        background: rgba(255,255,255,.78);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .overlay-content {
        text-align: center;
        color: var(--ink-2);
        font-size: 12.5px;
    }

    .spinner {
        width: 34px;
        height: 34px;
        border: 3px solid var(--line);
        border-top-color: var(--brand);
        border-radius: 50%;
        margin: 0 auto 10px;
        animation: spin .8s linear infinite;
    }

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }

    .statusbar {
        height: 28px;
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 0 14px;
        background: var(--chrome);
        border-top: 1px solid var(--line);
        font-size: 11px;
        color: var(--ink-3);
    }

        .statusbar .grow {
            flex: 1;
        }

        .statusbar .edicao-estado {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            white-space: nowrap;
        }

            .statusbar .edicao-estado.is-pending {
                color: #b45309;
                font-weight: 600;
            }

                .statusbar .edicao-estado.is-pending i {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #f59e0b;
                }

        .statusbar .ia-highlight-legend {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            color: var(--ink-2);
        }

            .statusbar .ia-highlight-legend i {
                width: 14px;
                height: 8px;
                border-radius: 2px;
                background: #00ffff;
                box-shadow: inset 0 0 0 1px rgba(15, 23, 42, .14);
            }

    /* RESIZER */
    .resizer {
        width: 5px;
        flex: 0 0 auto;
        cursor: col-resize;
        position: relative;
        z-index: 10;
        background: var(--canvas);
    }

        .resizer::after {
            content: "";
            position: absolute;
            inset: 0 2px;
            background: var(--brand);
            opacity: 0;
            transition: opacity .15s;
        }

        .resizer:hover::after,
        .resizer.dragging::after {
            opacity: 1;
        }

    /* ASSISTENTE */
    .assistant {
        flex: 0 0 auto;
        background: var(--paper);
        border-left: 1px solid var(--line);
        display: flex;
        flex-direction: column;
        min-height: 0;
    }

    .ahead {
        height: 46px;
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 9px;
        padding: 0 8px 0 14px;
        border-bottom: 1px solid var(--line-soft);
    }

        .ahead .spark {
            width: 22px;
            height: 22px;
            border-radius: 7px;
            background: var(--ink);
            color: #fff;
            display: grid;
            place-items: center;
            flex: 0 0 auto;
        }

        .ahead h2 {
            margin: 0;
            font-size: 13px;
            font-weight: 600;
        }

        .ahead .count {
            font-size: 10.5px;
            font-weight: 600;
            color: var(--brand-700);
            background: var(--brand-50);
            border: 1px solid #bfdbfe;
            border-radius: 20px;
            padding: 1px 8px;
        }

        .ahead .push {
            flex: 1;
        }

        .ahead button {
            width: 28px;
            height: 28px;
            border-radius: var(--r-sm);
            display: grid;
            place-items: center;
            color: var(--ink-3);
        }

            .ahead button:hover {
                background: var(--chrome);
                color: var(--ink);
            }

    .chat-status {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 14px;
        background: var(--brand-50);
        color: var(--brand-700);
        font-size: 12px;
        border-bottom: 1px solid #dbeafe;
    }

    .pulse {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--brand);
        animation: pulse 1.1s ease-in-out infinite;
        flex: 0 0 auto;
    }

    .thread {
        flex: 1;
        overflow-y: auto;
        padding: 16px 14px 8px;
        display: flex;
        flex-direction: column;
        gap: 18px;
        background: var(--chrome);
    }

    .intro {
        padding: 4px 2px;
    }

        .intro p {
            margin: 0 0 6px;
            font-size: 12.8px;
            color: var(--ink-2);
            line-height: 1.55;
        }

        .intro p.lead {
            font-weight: 600;
            color: var(--ink);
            font-size: 13.5px;
        }

    .chips {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-top: 14px;
    }

    .chip {
        text-align: left;
        border: 1px solid var(--line);
        border-radius: var(--r-md);
        padding: 9px 11px;
        font-size: 12.4px;
        color: var(--ink-2);
        line-height: 1.4;
        display: flex;
        gap: 9px;
        align-items: flex-start;
        background: var(--paper);
    }

        .chip:hover:not(:disabled) {
            border-color: var(--brand);
            background: var(--brand-50);
            color: var(--brand-700);
        }

        .chip:disabled {
            opacity: .55;
            cursor: not-allowed;
        }

        .chip svg {
            flex: 0 0 auto;
            margin-top: 1px;
            color: var(--ink-4);
        }

        .chip:hover:not(:disabled) svg {
            color: var(--brand);
        }

    .msg-group {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .msg {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

        .msg.user {
            align-items: flex-end;
        }

            .msg.user .bubble {
                background: var(--ink);
                color: #e8eef6;
                border-radius: 12px 12px 3px 12px;
                padding: 9px 12px;
                font-size: 12.8px;
                line-height: 1.5;
                max-width: 90%;
                white-space: pre-wrap;
            }

            .msg.user .stamp {
                font-size: 10.5px;
                color: var(--ink-4);
            }

        .msg.bot .who {
            display: flex;
            align-items: center;
            gap: 7px;
            font-size: 11px;
            font-weight: 600;
            color: var(--ink-3);
        }

            .msg.bot .who i {
                width: 16px;
                height: 16px;
                border-radius: 5px;
                background: var(--ink);
                display: grid;
                place-items: center;
                color: #fff;
            }

        .msg.bot .text {
            font-size: 12.9px;
            line-height: 1.58;
            color: var(--ink-2);
            white-space: pre-wrap;
        }

            .msg.bot .text.pending {
                display: flex;
                align-items: center;
                gap: 9px;
                color: var(--ink-3);
                font-style: italic;
            }

    .typing {
        display: inline-flex;
        gap: 3px;
    }

        .typing i {
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background: var(--brand);
            animation: bounce 1s infinite;
        }

            .typing i:nth-child(2) {
                animation-delay: .15s;
            }

            .typing i:nth-child(3) {
                animation-delay: .3s;
            }

    @keyframes bounce {
        0%, 60%, 100% {
            opacity: .3;
        }

        30% {
            opacity: 1;
        }
    }

    .callout {
        display: flex;
        gap: 8px;
        align-items: flex-start;
        border-radius: var(--r-md);
        padding: 9px 11px;
        font-size: 12.4px;
        line-height: 1.45;
        border: 1px solid;
    }

        .callout.error {
            background: var(--danger-50);
            border-color: var(--danger-line);
            color: var(--danger);
        }

        .callout.ok {
            background: var(--ok-50);
            border-color: #bbf7d0;
            color: var(--ok);
        }

        .callout.neutral {
            background: var(--chrome);
            border-color: var(--line);
            color: var(--ink-3);
        }

        .callout.info,
        .callout.question {
            background: var(--brand-50);
            border-color: #bfdbfe;
            color: var(--ink-2);
        }

        .callout.info > div,
        .callout.question > div {
            display: flex;
            min-width: 0;
            flex-direction: column;
            gap: 3px;
        }

        .callout svg {
            flex: 0 0 auto;
            margin-top: 2px;
        }

    .proposal {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .proposal-summary {
        margin: 0 0 2px;
        color: var(--ink-2);
        font-size: 12.8px;
        line-height: 1.5;
    }

    .proposal-evidence {
        padding: 8px 9px;
        border: 1px solid var(--line);
        border-radius: 6px;
        background: var(--chrome);
        color: var(--ink-3);
        font-size: 11px;
    }

    .evidence-list {
        margin: 5px 0 0;
        padding-left: 18px;
        color: inherit;
        font-size: 10.8px;
        line-height: 1.45;
    }

        .evidence-list li + li {
            margin-top: 3px;
        }

    .diff-card {
        display: flex;
        gap: 9px;
        padding: 10px;
        margin: 0;
        border: 1px solid var(--line);
        border-radius: var(--r-md);
        background: var(--paper);
        cursor: pointer;
        transition: border-color .15s, box-shadow .15s;
    }

        .diff-card.selected {
            border-color: #93c5fd;
            box-shadow: 0 0 0 2px rgba(37,99,235,.08);
        }

        .diff-card.is-aplicada {
            border-color: #bbf7d0;
        }

        .diff-card.is-descartada {
            opacity: .62;
        }

        .diff-card input {
            margin: 3px 0 0;
            accent-color: var(--brand);
            flex: 0 0 auto;
        }

    .diff-content {
        min-width: 0;
        flex: 1;
    }

        .diff-content header {
            display: flex;
            align-items: center;
            gap: 7px;
            margin-bottom: 8px;
        }

    .diff-tag {
        padding: 2px 6px;
        border-radius: 4px;
        background: var(--brand-50);
        color: var(--brand-700);
        font-size: 9.5px;
        font-weight: 700;
        letter-spacing: .04em;
        text-transform: uppercase;
    }

        .diff-tag.tipo-remover,
        .diff-tag.tipo-removersubarvore,
        .diff-tag.tipo-removerparagrafo,
        .diff-tag.tipo-removertabela,
        .diff-tag.tipo-removerimagem {
            background: var(--danger-50);
            color: var(--danger);
        }

        .diff-tag.tipo-inserir {
            background: var(--ok-50);
            color: var(--ok);
        }

        .diff-tag.tipo-formatar {
            background: #eff6ff;
            color: #1d4ed8;
        }

    .diff-location {
        color: var(--ink-3);
        font-size: 10.5px;
        font-family: monospace;
    }

    .diff-badge {
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 9.5px;
        font-weight: 700;
        letter-spacing: .03em;
        text-transform: uppercase;
    }

        .diff-badge.baixa {
            background: #fef3c7;
            color: #b45309;
        }

        .diff-badge.sem-evidencia {
            background: var(--danger-50);
            color: var(--danger);
        }

    .diff-card.is-sem-evidencia {
        opacity: .82;
        border-style: dashed;
    }

    .diff-state {
        margin-left: auto;
        color: var(--ink-3);
        font-size: 10px;
        font-weight: 600;
    }

    .diff-row {
        display: flex;
        gap: 7px;
        margin-top: 5px;
        padding: 6px 7px;
        border-radius: 5px;
        font-size: 11.8px;
        line-height: 1.45;
        white-space: pre-wrap;
    }

        .diff-row b {
            flex: 0 0 auto;
            font-family: monospace;
        }

        .diff-row.old {
            background: var(--danger-50);
            color: #7f1d1d;
        }

        .diff-row.new {
            background: var(--ok-50);
            color: #166534;
        }

        /* Contexto do ponto de inserção: tom neutro para não sugerir exclusão. */
        .diff-row.anchor {
            background: var(--chrome);
            color: var(--ink-2);
            font-size: 11px;
        }

        .diff-row.format {
            background: #eff6ff;
            color: #1e3a8a;
        }

    .affected {
        margin-top: 8px;
        padding: 8px;
        border-radius: 6px;
        background: var(--chrome);
        color: var(--ink-2);
        font-size: 11px;
    }

        .affected ul {
            margin: 5px 0 0;
            padding-left: 17px;
            max-height: 116px;
            overflow: auto;
        }

        .affected li {
            margin: 2px 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .affected small {
            display: block;
            margin-top: 4px;
            color: var(--ink-3);
        }

    .diff-warning {
        display: flex;
        align-items: flex-start;
        gap: 6px;
        margin-top: 7px;
        padding: 6px 7px;
        border: 1px solid #fde68a;
        border-radius: 5px;
        background: #fffbeb;
        color: #92400e;
        font-size: 10.8px;
        line-height: 1.4;
    }

        .diff-warning svg {
            flex: 0 0 auto;
            margin-top: 1px;
        }

    .proposal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 7px;
        padding-top: 2px;
    }

        .proposal-actions .mini.danger {
            color: var(--danger);
        }

        .proposal-actions button:disabled {
            opacity: .5;
            cursor: not-allowed;
        }

    /* COMPOSER */
    .composer {
        flex: 0 0 auto;
        padding: 10px 14px 14px;
        border-top: 1px solid var(--line-soft);
        background: var(--paper);
    }

    .inputwrap {
        border: 1px solid var(--line);
        border-radius: var(--r-lg);
        background: var(--paper);
        padding: 8px 8px 6px;
        transition: border-color .15s, box-shadow .15s;
    }

        .inputwrap:focus-within {
            border-color: var(--brand);
            box-shadow: 0 0 0 3px rgba(37,99,235,.10);
        }

        .inputwrap.disabled {
            background: var(--chrome);
            opacity: .75;
        }

        .inputwrap textarea {
            width: 100%;
            border: 0;
            outline: 0;
            resize: none;
            font: inherit;
            font-size: 13px;
            line-height: 1.5;
            color: var(--ink);
            background: none;
            max-height: 140px;
            min-height: 38px;
            padding: 2px 2px 0;
        }

            .inputwrap textarea::placeholder {
                color: var(--ink-4);
            }

    .crow {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 2px;
    }

        .crow .grow {
            flex: 1;
        }

    .counter {
        font-size: 10.5px;
        color: var(--ink-4);
        padding-left: 2px;
    }

    .send {
        width: 30px;
        height: 30px;
        border-radius: 8px;
        background: var(--brand);
        color: #fff;
        display: grid;
        place-items: center;
        flex: 0 0 auto;
    }

        .send:hover:not(:disabled) {
            background: var(--brand-700);
        }

        .send:disabled {
            background: var(--ink-4);
            cursor: not-allowed;
        }

    .hint {
        font-size: 10.5px;
        color: var(--ink-4);
        margin: 7px 2px 0;
        text-align: center;
    }

    /* BLOCKER */
    .ia-blocker {
        flex: 1;
        display: grid;
        place-items: center;
        padding: 24px;
    }

    .blocker-card {
        background: var(--paper);
        border: 1px solid var(--line);
        border-radius: var(--r-lg);
        box-shadow: var(--shadow-2);
        padding: 28px 32px;
        text-align: center;
        max-width: 460px;
    }

    .blocker-icon {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: var(--danger-50);
        color: var(--danger);
        display: grid;
        place-items: center;
        margin: 0 auto 14px;
    }

    .blocker-card h3 {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
    }

    .blocker-card .motivo {
        color: var(--ink-3);
        margin: 8px 0 18px;
        font-size: 12.8px;
        line-height: 1.5;
    }

    .blocker-actions {
        display: flex;
        gap: 8px;
        justify-content: center;
    }

    /* REOPEN + TOAST */
    .reopen {
        position: fixed;
        right: 0;
        bottom: 64px;
        z-index: 50;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        height: 38px;
        padding: 0 14px 0 12px;
        border: 0;
        border-radius: 10px 0 0 10px;
        background: var(--ink);
        color: #fff;
        font-size: 12.5px;
        font-weight: 600;
        cursor: pointer;
        box-shadow: var(--shadow-2);
    }

    .toast {
        position: fixed;
        left: 50%;
        bottom: 26px;
        transform: translate(-50%, 12px);
        z-index: 60;
        background: var(--ink);
        color: #fff;
        font-size: 12.5px;
        font-weight: 500;
        padding: 10px 14px;
        border-radius: 10px;
        box-shadow: var(--shadow-2);
        opacity: 0;
        pointer-events: none;
        transition: opacity .18s, transform .18s;
        display: flex;
        align-items: center;
        gap: 9px;
        max-width: min(560px, 90vw);
    }

        .toast.on {
            opacity: 1;
            transform: translate(-50%, 0);
        }

        .toast.erro {
            background: var(--danger);
        }

    .thread::-webkit-scrollbar {
        width: 10px;
    }

    .thread::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 10px;
        border: 3px solid transparent;
        background-clip: content-box;
    }

    @media (max-width: 1100px) {
        .assistant {
            position: fixed;
            right: 0;
            top: var(--appbar-height);
            bottom: 0;
            z-index: 55;
            box-shadow: var(--shadow-2);
        }

        .resizer {
            display: none;
        }
    }

    @media (max-width: 720px) {
        .assistant {
            width: 100% !important;
        }

        .doc-id h1 {
            font-size: 13px;
        }
    }
</style>
